/**
 * Calculate McArthur Forest Fire Danger Index (FFDI)
 * 
 * @param temperature - Temperature in Celsius
 * @param relativeHumidity - Relative humidity as percentage (0-100)
 * @param windSpeed - Wind speed in km/h
 * @param droughtFactor - Drought factor (0-10), defaults to 10 (severe drought)
 * @returns Fire danger rating (FFDI value)
 */
export const calculate_fire_danger_rating = (
  temperature: number,
  relativeHumidity: number,
  windSpeed: number,
  droughtFactor: number = 10
): number => {
  if (relativeHumidity <= 0 || temperature <= 0 || windSpeed < 0 || droughtFactor <= 0) {
    return 0;
  }

  const rh = Math.min(100, Math.max(0, relativeHumidity));
  const t = temperature;
  const u10 = windSpeed;
  const d = droughtFactor;

  // Fuel Moisture Content (MC) [Eqn. 5.21]
  // MC = 5.658 + 0.04651 RH + 0.0003151 RH^3 T^-1 − 0.184 T^0.77
  const mc =
    5.658 +
    0.04651 * rh +
    0.0003151 * Math.pow(rh, 3) * Math.pow(t, -1) -
    0.184 * Math.pow(t, 0.77);

  if (!Number.isFinite(mc) || mc <= 0) {
    return 0;
  }

  // Forest Fire Danger Index (FFDI) [Eqn. 5.20]
  // FFDI = 34.81 exp(0.987 ln(D)) MC^-2.1 exp(0.0234 U10)
  const ffdi =
    34.81 *
    Math.exp(0.987 * Math.log(d)) *
    Math.pow(mc, -2.1) *
    Math.exp(0.0234 * u10);

  if (!Number.isFinite(ffdi) || ffdi < 0) {
    return 0;
  }

  return Math.round(ffdi * 100) / 100;
};

/**
 * Get fire danger category from FFDI value using AFDRS classifications
 */
export const get_fire_danger_category = (ffdi: number): string => {
  if (ffdi < 12) return 'No Rating';
  if (ffdi < 24) return 'Moderate';
  if (ffdi < 50) return 'High';
  if (ffdi < 100) return 'Extreme';
  return 'Catastrophic';
};

/**
 * Get fire danger category with label and color for a given FFDI rating
 * @param rating - Fire danger rating (FFDI value)
 * @returns Object with label and color for the category
 */
export const getDangerCategory = (rating: number): { label: string; color: string } => {
  if (rating >= 100) return { label: 'CATASTROPHIC', color: '#d32f2f' }; // Red
  if (rating >= 50) return { label: 'EXTREME', color: '#ff6b35' }; // Orange
  if (rating >= 24) return { label: 'HIGH', color: '#ffd700' }; // Yellow
  if (rating >= 12) return { label: 'MODERATE', color: '#66bb6a' }; // Green
  return { label: 'NO RATING', color: '#9e9e9e' }; // Gray
};

export interface FdiThreshold {
  label: string;
  value: number;
  color: string;
}

/**
 * Danger thresholds — the lower bound of each AFDRS category with its tier
 * colour. Single source of truth for the dashed reference lines / legends in
 * the Fire Danger Index and Seasonal Outlook views. Colours match
 * {@link getDangerCategory}.
 */
export const FDI_THRESHOLDS: FdiThreshold[] = [
  { label: 'Moderate', value: 12, color: '#66bb6a' },
  { label: 'High', value: 24, color: '#ffd700' },
  { label: 'Extreme', value: 50, color: '#ff6b35' },
  { label: 'Catastrophic', value: 100, color: '#d32f2f' },
];
